import type { Domain, JourneyInstance, Profile } from "./types";
import { cantonService, type CantonTopic } from "./cantons";

export type ResourceKind =
  | "official-service"
  | "official-guide"
  | "independent-tool"
  | "nonprofit-tool"
  | "commercial-comparison"
  | "commercial-provider";

export interface SwissResource {
  id: string;
  title: string;
  provider: string;
  kind: ResourceKind;
  url: string;
  description: string;
  cta: string;
  caution?: string;
  domains: Domain[];
  rulePrefixes: string[];
  applies: (profile: Profile) => boolean;
  score: (profile: Profile) => number;
}

export const resources: SwissResource[] = [
  {
    id: "priminfo",
    title: "Priminfo premium calculator",
    provider: "Federal Office of Public Health",
    kind: "official-service",
    url: "https://www.priminfo.admin.ch/en/praemien",
    description: "Compare approved compulsory health-insurance premiums for your postcode, age, model and deductible.",
    cta: "Compare official premiums",
    caution: "Use this as the neutral price baseline before requesting any commercial quote.",
    domains: ["Health"],
    rulePrefixes: ["health-", "accident-", "work-cover"],
    applies: () => true,
    score: (profile) => profile.healthInsuranceConfirmed === "no" ? 100 : 78,
  },
  {
    id: "health-switch-guide",
    title: "How to change basic health insurance",
    provider: "Swiss Confederation - ch.ch",
    kind: "official-guide",
    url: "https://www.ch.ch/en/health/health-insurance/changing-health-insurance/",
    description: "Read the official switching steps, notice rules and key difference between basic and supplementary insurance.",
    cta: "Read the official guide",
    domains: ["Health"],
    rulePrefixes: ["health-review", "health-enrollment"],
    applies: () => true,
    score: () => 72,
  },
  {
    id: "comparis-health",
    title: "Health-insurance comparison",
    provider: "Comparis",
    kind: "commercial-comparison",
    url: "https://en.comparis.ch/krankenkassen/default",
    description: "Compare premiums, models and service ratings, and optionally request quotes from participating providers.",
    cta: "Explore the comparison",
    caution: "Commercial service. Results can include advertisements and quote requests share data with the service and providers.",
    domains: ["Health"],
    rulePrefixes: ["health-", "accident-"],
    applies: () => true,
    score: () => 68,
  },
  {
    id: "emoving",
    title: "Register a move online",
    provider: "eMovingCH",
    kind: "official-service",
    url: "https://www.eumzug.swiss/eumzugngx/global",
    description: "Check whether your old and new communes support the official online move-registration service.",
    cta: "Start move registration",
    caution: "Not every commune or personal situation is supported; the service tells you when an in-person route is required.",
    domains: ["Residency", "Home"],
    rulePrefixes: ["move-registration"],
    applies: (profile) => profile.nextEvent === "moving",
    score: () => 98,
  },
  {
    id: "cityparity-relocation",
    title: "Compare an international move",
    provider: "CityParity",
    kind: "independent-tool",
    url: "https://cityparity.com/",
    description: "Compare the gross salary needed in another city after recurring differences such as tax, housing, healthcare, childcare and employment benefits.",
    cta: "Run a private salary comparison",
    caution: "Independent external tool. CH/OS does not send it your profile. Enter details there yourself, then add one-off moving costs and verify permit, tax, insurance and pension consequences separately.",
    domains: ["Residency", "Work", "Money", "Home"],
    rulePrefixes: ["departure-", "permanent-departure", "move-registration", "job-", "finance-baseline"],
    applies: (profile) =>
      profile.nextEvent === "moving"
      || profile.nextEvent === "leaving"
      || profile.livesInSwitzerland === "no"
      || Boolean(profile.departureDestination),
    score: (profile) => profile.nextEvent === "leaving" || profile.livesInSwitzerland === "no" ? 97 : 76,
  },
  {
    id: "public-authorities",
    title: "Find the competent authority",
    provider: "Swiss Confederation - ch.ch",
    kind: "official-service",
    url: "https://www.ch.ch/en/safety-and-justice/addresses-of-administrative-authorities",
    description: "Find official federal, cantonal and communal directories and the relevant online administration portal.",
    cta: "Find your authority",
    domains: ["Residency", "Tax", "Mobility", "Protection"],
    rulePrefixes: ["move-", "permit-", "citizenship-", "tax-", "vehicle-", "mobility-", "protection-"],
    applies: () => true,
    score: () => 60,
  },
  {
    id: "cantonal-road-offices",
    title: "Find your cantonal road traffic office",
    provider: "Association of Cantonal Road Traffic Offices (asa)",
    kind: "official-service",
    url: "https://asa.ch/strassenverkehrsaemter/adressen/",
    description: "Open the national directory and go directly to the road traffic office responsible for inspections, registration and vehicle tax in your canton.",
    cta: "Find the right vehicle office",
    caution: "The directory opens in German and links onward to each canton’s official service.",
    domains: ["Mobility"],
    rulePrefixes: ["vehicle-", "mobility-review"],
    applies: (profile) => Boolean(profile.vehicle && profile.vehicle !== "none"),
    score: () => 96,
  },
  {
    id: "cantonal-migration",
    title: "Cantonal migration offices",
    provider: "State Secretariat for Migration",
    kind: "official-service",
    url: "https://www.sem.admin.ch/sem/de/home/sem/kontakt/kantonale_behoerden/adressen_kantone_und.html",
    description: "Open the federal directory of cantonal migration and labour-market authorities for permit-specific confirmation.",
    cta: "Find your cantonal office",
    caution: "The directory is multilingual but this overview page opens in German.",
    domains: ["Residency", "Work"],
    rulePrefixes: ["permit-", "citizenship-"],
    applies: (profile) => Boolean(profile.residency && profile.residency !== "swiss"),
    score: (profile) => profile.permitGoal && profile.permitGoal !== "none" ? 96 : 64,
  },
  {
    id: "swiss-tax-calculator",
    title: "Swiss tax calculator",
    provider: "Federal Tax Administration",
    kind: "official-service",
    url: "https://swisstaxcalculator.estv.admin.ch/",
    description: "Model federal, cantonal and communal income and wealth tax using the official calculator.",
    cta: "Estimate your taxes",
    caution: "An estimate is not a filing decision; use the competent cantonal authority for binding treatment and forms.",
    domains: ["Tax", "Pension"],
    rulePrefixes: ["tax-", "pillar3a-", "pillar2-buyin"],
    applies: (profile) => Boolean(profile.taxMode),
    score: (profile) => profile.taxMode === "source" ? 90 : 76,
  },
  {
    id: "tax-return-guide",
    title: "Swiss tax-return guide",
    provider: "Swiss Confederation - ch.ch",
    kind: "official-guide",
    url: "https://www.ch.ch/en/taxes-and-finances/tax-return/",
    description: "Start with the official overview, then follow the link to your canton's filing process and deadline.",
    cta: "Open the official guide",
    caution: "Deadlines, extensions and forms are cantonal; confirm the final details on your canton’s website.",
    domains: ["Tax"],
    rulePrefixes: ["tax-season", "tax-at-source-", "tax-source-"],
    applies: (profile) => Boolean(profile.taxMode),
    score: () => 70,
  },
  {
    id: "ahv-statement",
    title: "Order your AHV account statement",
    provider: "OASI/DI Information Centre",
    kind: "official-service",
    url: "https://www.ahv-iv.ch/en/Forms/Statement-of-the-individual-account",
    description: "Request the free individual-account statement used to check contribution years and recorded income.",
    cta: "Open the official request",
    domains: ["Pension"],
    rulePrefixes: ["ahv-"],
    applies: () => true,
    score: (profile) => profile.ahvStatementDate ? 42 : 86,
  },
  {
    id: "ahv-escal",
    title: "Estimate your AHV pension",
    provider: "OASI/DI Information Centre",
    kind: "official-service",
    url: "https://www.ahv-iv.ch/en/Forms/Online-pension-estimate-ESCAL",
    description: "Use ESCAL for a non-binding old-age pension estimate based on the information you enter.",
    cta: "Run an ESCAL estimate",
    caution: "The result is indicative and does not replace an official pension forecast.",
    domains: ["Pension"],
    rulePrefixes: ["ahv-", "pillar-stack", "annual-reset"],
    applies: (profile) => Boolean(profile.birthYear && new Date().getFullYear() - profile.birthYear >= 45),
    score: () => 72,
  },
  {
    id: "vested-benefit-search",
    title: "Search for forgotten Pillar 2 assets",
    provider: "LOB Guarantee Fund Central Office",
    kind: "official-service",
    url: "https://sfbvg.ch/en/tasks/enquiry-about-occupational-benefit-credit-balances/online-form",
    description: "Submit the official enquiry used to locate occupational-pension or vested-benefit balances.",
    cta: "Open the official search",
    domains: ["Pension", "Work"],
    rulePrefixes: ["pillar2-", "job-", "unemployment-", "annual-reset"],
    applies: (profile) => (
      profile.nextEvent === "job-change"
      || profile.employment === "unemployed"
      || Boolean(profile.employmentEndDate)
    ) && profile.employment !== "retired"
      && profile.employment !== "caregiver"
      && profile.employment !== "inactive",
    score: () => 93,
  },
  {
    id: "moneyland-3a",
    title: "Compare Pillar 3a options",
    provider: "moneyland.ch",
    kind: "commercial-comparison",
    url: "https://www.moneyland.ch/en/pillar-3a-comparison",
    description: "Compare 3a savings-account rates and fees before choosing a provider or moving an existing balance.",
    cta: "Compare 3a accounts",
    caution: "Commercial comparison service. Compare methodology, fees, risk and provider terms; past returns are not a recommendation.",
    domains: ["Pension", "Money"],
    rulePrefixes: ["pillar3a-", "pillar-stack"],
    applies: (profile) => profile.employment === "employed"
      || profile.employment === "self-employed"
      || profile.hasEmploymentContract === "yes",
    score: (profile) => profile.pillar3aContributed === undefined ? 82 : 66,
  },
  {
    id: "pillar3a-guide",
    title: "Official Pillar 3a overview",
    provider: "Swiss Confederation - ch.ch",
    kind: "official-guide",
    url: "https://www.ch.ch/en/retirement/old-age-pension/the-3rd-pillar/",
    description: "Check eligibility, contribution limits, tax treatment and permitted withdrawal cases before comparing products.",
    cta: "Read the official overview",
    domains: ["Pension"],
    rulePrefixes: ["pillar3a-", "pillar-stack"],
    applies: (profile) => profile.employment === "employed"
      || profile.employment === "self-employed"
      || profile.hasEmploymentContract === "yes",
    score: () => 74,
  },
  {
    id: "finma-authorised",
    title: "Verify an authorised institution",
    provider: "Swiss Financial Market Supervisory Authority",
    kind: "official-service",
    url: "https://www.finma.ch/en/finma-public/authorised-institutions-individuals-and-products/",
    description: "Check FINMA lists and investor-protection information before choosing a Swiss financial institution or product.",
    cta: "Open FINMA verification",
    caution: "A listing confirms regulatory status for the listed activity; it does not make a product suitable for you.",
    domains: ["Money", "Pension"],
    rulePrefixes: ["broker-", "investment-policy", "finance-baseline"],
    applies: () => true,
    score: () => 84,
  },
  {
    id: "moneyland-trading",
    title: "Compare online brokers",
    provider: "moneyland.ch",
    kind: "commercial-comparison",
    url: "https://www.moneyland.ch/en/online-trading-comparison",
    description: "Compare Swiss trading costs using your expected markets and transaction pattern.",
    cta: "Model broker costs",
    caution: "Commercial comparison. Coverage and assumptions can differ; verify the legal entity, custody, foreign-exchange costs and tax reporting separately.",
    domains: ["Money"],
    rulePrefixes: ["broker-", "investment-policy"],
    applies: (profile) => profile.investmentHorizon !== undefined,
    score: () => 75,
  },
  {
    id: "interactive-brokers",
    title: "Interactive Brokers",
    provider: "Interactive Brokers",
    kind: "commercial-provider",
    url: "https://www.interactivebrokers.com/en/pricing/commissions-home.php",
    description: "A widely used international broker example with broad market access and generally low published trading costs.",
    cta: "Review pricing and account terms",
    caution: "Commercial provider, not endorsed by CH/OS. Swiss residents generally use IBKR Pro. Compare legal entity, complexity, currency conversion, support, tax records and the risks of margin or advanced products.",
    domains: ["Money"],
    rulePrefixes: ["broker-", "investment-policy"],
    applies: (profile) => profile.brokerStatus === "none" || profile.brokerStatus === "foreign",
    score: () => 70,
  },
  {
    id: "finpension-3a",
    title: "finpension 3a",
    provider: "finpension",
    kind: "commercial-provider",
    url: "https://finpension.ch/en/invest-pillar-3a/",
    description: "A commercial invested Pillar 3a example to compare on allocation, all-in fees, foundation structure, cash handling and withdrawal terms.",
    cta: "Review provider details",
    caution: "Commercial provider, not endorsed or ranked by CH/OS. Verify current fees and compare on identical risk assumptions with other suitable providers.",
    domains: ["Pension", "Money"],
    rulePrefixes: ["pillar3a-strategy", "pillar3a-"],
    applies: (profile) => profile.employment === "employed"
      || profile.employment === "self-employed"
      || profile.hasEmploymentContract === "yes",
    score: () => 83,
  },
  {
    id: "viac-3a",
    title: "VIAC Pillar 3a",
    provider: "VIAC",
    kind: "commercial-provider",
    url: "https://viac.ch/en/products/pillar-3a/fees/",
    description: "A commercial invested Pillar 3a example to compare on allocation, all-in fees, foundation structure, cash handling and withdrawal terms.",
    cta: "Review provider details",
    caution: "Commercial provider, not endorsed or ranked by CH/OS. Verify current fees and compare on identical risk assumptions with other suitable providers.",
    domains: ["Pension", "Money"],
    rulePrefixes: ["pillar3a-strategy", "pillar3a-"],
    applies: (profile) => profile.employment === "employed"
      || profile.employment === "self-employed"
      || profile.hasEmploymentContract === "yes",
    score: () => 83,
  },
  {
    id: "fta-usa",
    title: "Swiss–US withholding forms",
    provider: "Federal Tax Administration",
    kind: "official-guide",
    url: "https://www.estv.admin.ch/de/usa",
    description: "Open the official Swiss overview for W-8BEN and additional US withholding-tax forms.",
    cta: "Open the official forms guide",
    domains: ["Tax", "Money"],
    rulePrefixes: ["w8ben-", "foreign-dividend-"],
    applies: (profile) => profile.usesUSListedSecurities === "yes",
    score: () => 96,
  },
  {
    id: "fta-da1",
    title: "DA-1 and R-US 164 forms",
    provider: "Federal Tax Administration",
    kind: "official-service",
    url: "https://www.estv.admin.ch/de/formulare-wegleitungen-direkte-bundessteuer",
    description: "Find the official forms used for eligible foreign withholding-tax credits and additional US withholding.",
    cta: "Open official tax forms",
    caution: "The canton controls the filing workflow and deadline. Use the competent cantonal tax portal for the actual return.",
    domains: ["Tax"],
    rulePrefixes: ["foreign-dividend-"],
    applies: (profile) => profile.usesUSListedSecurities === "yes",
    score: () => 98,
  },
  {
    id: "rent-calculator",
    title: "Check a possible rent reduction",
    provider: "Swiss Tenants' Association",
    kind: "nonprofit-tool",
    url: "https://www.mieterverband.ch/mietrecht/waehrend-der-miete/mietzinssenkung/",
    description: "Use the tenant association's guide and calculator before writing to your landlord about the reference rate.",
    cta: "Check the rent calculation",
    caution: "The tool is in German. Individual leases, inflation and operating-cost changes can alter the result.",
    domains: ["Home"],
    rulePrefixes: ["rent-"],
    applies: (profile) => profile.housing === "renter",
    score: (profile) => profile.leaseReferenceRate === undefined ? 84 : 94,
  },
  {
    id: "comparis-household",
    title: "Compare household and liability cover",
    provider: "Comparis",
    kind: "commercial-comparison",
    url: "https://www.comparis.ch/hausrat-versicherung/default",
    description: "Compare household-contents and personal-liability premiums and coverage from participating insurers.",
    cta: "Compare cover",
    caution: "Commercial service. Not every insurer participates, advertisements may appear, and quote requests share data with providers.",
    domains: ["Home", "Protection"],
    rulePrefixes: ["home-protection", "personal-liability"],
    applies: (profile) => profile.housing === "renter" || profile.personalLiabilityCovered !== "yes",
    score: (profile) => profile.personalLiabilityCovered === "no" ? 91 : 62,
  },
  {
    id: "job-room",
    title: "Sign on with RAV and use Job-Room",
    provider: "work.swiss",
    kind: "official-service",
    url: "https://www.arbeit.swiss/en/jobseekers/signing-on-and-registration",
    description: "Use the public employment-service route to sign on with RAV and access Job-Room services.",
    cta: "Open the official process",
    domains: ["Work"],
    rulePrefixes: ["rav-", "job-", "unemployment-"],
    applies: (profile) => (
      profile.employment === "unemployed"
      || profile.nextEvent === "job-change"
      || Boolean(profile.employmentEndDate)
    ) && profile.employment !== "retired"
      && profile.employment !== "caregiver"
      && profile.employment !== "inactive",
    score: (profile) => profile.employment === "unemployed" ? 100 : 88,
  },
  {
    id: "budgetberatung",
    title: "Build a realistic Swiss budget",
    provider: "Budgetberatung Schweiz",
    kind: "nonprofit-tool",
    url: "https://budgetberatung.ch/budgetrechner",
    description: "Create a household budget using Swiss categories, examples and guidance from the nonprofit umbrella organisation.",
    cta: "Open the budget tool",
    caution: "The external calculator stores entries under a retrieval code; review its privacy note before entering personal figures.",
    domains: ["Money"],
    rulePrefixes: ["cash-buffer", "contract-sweep", "annual-reset"],
    applies: () => true,
    score: (profile) => (profile.cashBufferMonths ?? 0) < 3 ? 88 : 58,
  },
  {
    id: "via-vignette",
    title: "Buy or verify an e-vignette",
    provider: "Federal Office for Customs and Border Security",
    kind: "official-service",
    url: "https://via.admin.ch/shop/dashboard",
    description: "Use the official Via portal for the Swiss motorway e-vignette, including motorcycles.",
    cta: "Open the official Via portal",
    domains: ["Mobility"],
    rulePrefixes: ["mobility-review"],
    applies: (profile) => Boolean(profile.vehicle && profile.vehicle !== "none"),
    score: () => 68,
  },
  {
    id: "comparis-motorbike",
    title: "Compare motorcycle insurance",
    provider: "Comparis",
    kind: "commercial-comparison",
    url: "https://en.comparis.ch/motorradversicherung/default",
    description: "Compare motorcycle liability and optional casco offers from participating insurers.",
    cta: "Compare motorcycle cover",
    caution: "Commercial service. The comparison includes participating providers and may facilitate quote requests.",
    domains: ["Mobility"],
    rulePrefixes: ["vehicle-liability-", "mobility-review"],
    applies: (profile) => profile.vehicle === "motorbike" || profile.vehicle === "both",
    score: () => 74,
  },
  {
    id: "comparis-car",
    title: "Compare car insurance",
    provider: "Comparis",
    kind: "commercial-comparison",
    url: "https://en.comparis.ch/autoversicherung/default",
    description: "Compare car liability and optional casco offers from participating insurers.",
    cta: "Compare car cover",
    caution: "Commercial service. The comparison includes participating providers and may facilitate quote requests.",
    domains: ["Mobility"],
    rulePrefixes: ["vehicle-liability-", "mobility-review"],
    applies: (profile) => profile.vehicle === "car" || profile.vehicle === "both",
    score: () => 73,
  },
  {
    id: "alertswiss",
    title: "Create an emergency plan",
    provider: "Alertswiss - Federal Office for Civil Protection",
    kind: "official-service",
    url: "https://www.alert.swiss/en/precaution/emergency-plan.html",
    description: "Create a personal emergency plan and find the official multilingual checklist for your household.",
    cta: "Build an emergency plan",
    domains: ["Protection"],
    rulePrefixes: ["protection-", "annual-reset"],
    applies: () => true,
    score: () => 48,
  },
];

function matchesRule(resource: SwissResource, ruleId: string): boolean {
  return resource.rulePrefixes.some((prefix) => ruleId.startsWith(prefix));
}

function cantonTopicForJourney(journey: JourneyInstance): CantonTopic | null {
  if (journey.rule.id.includes("premium-reduction")) return "premium";
  if (journey.rule.domain === "Tax") return "tax";
  if (journey.rule.domain === "Residency") return "migration";
  if (journey.rule.domain === "Mobility") return "road";
  return null;
}

function directCantonResource(
  journey: JourneyInstance,
  profile: Profile,
): SwissResource | null {
  const topic = cantonTopicForJourney(journey);
  if (!topic) return null;
  const service = cantonService(profile.canton, topic);
  if (!service) return null;
  return {
    id: `canton-${profile.canton?.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${topic}`,
    title: service.label,
    provider: service.authority,
    kind: "official-service",
    url: service.url,
    description: `Open the official ${profile.canton} service that handles this action instead of starting from a general directory.`,
    cta: "Open the competent service",
    caution: "Confirm the personal deadline and required documents shown by the authority.",
    domains: [journey.rule.domain],
    rulePrefixes: [journey.rule.id],
    applies: () => true,
    score: () => 120,
  };
}

export function resourceKindLabel(kind: ResourceKind): string {
  return {
    "official-service": "Official service",
    "official-guide": "Official guide",
    "independent-tool": "Independent external tool",
    "nonprofit-tool": "Independent nonprofit",
    "commercial-comparison": "Commercial comparison",
    "commercial-provider": "Commercial provider",
  }[kind];
}

export function resourcesForProfile(
  profile: Profile,
  activeJourneys: JourneyInstance[] = [],
): SwissResource[] {
  const activeIds = activeJourneys
    .filter((journey) => journey.progress.status === "open")
    .map((journey) => journey.rule.id);

  return resources
    .filter((resource) => resource.applies(profile))
    .map((resource) => ({
      resource,
      score: resource.score(profile) + (activeIds.some((id) => matchesRule(resource, id)) ? 45 : 0),
    }))
    .sort((a, b) => b.score - a.score || a.resource.id.localeCompare(b.resource.id))
    .map(({ resource }) => resource);
}

export function resourcesForJourney(
  journey: JourneyInstance,
  profile: Profile,
): SwissResource[] {
  const cantonDirect = directCantonResource(journey, profile);
  const direct = [
    ...(cantonDirect ? [cantonDirect] : []),
    ...resources
    .filter((resource) => resource.applies(profile) && matchesRule(resource, journey.rule.id))
    .sort((a, b) => {
      const officialDifference =
        Number(b.kind === "official-service" || b.kind === "official-guide")
        - Number(a.kind === "official-service" || a.kind === "official-guide");
      return officialDifference || b.score(profile) - a.score(profile);
    }),
  ].filter((resource, index, all) => all.findIndex((item) => item.url === resource.url) === index);

  if (direct.length >= 2) return direct.slice(0, 3);

  const fallback = resourcesForProfile(profile)
    .filter((resource) =>
      !direct.some((item) => item.id === resource.id)
      && resource.domains.includes(journey.rule.domain));

  return [...direct, ...fallback].slice(0, 3);
}

export function resourceById(id: string): SwissResource | undefined {
  return resources.find((resource) => resource.id === id);
}

export function resourcesForDomains(
  profile: Profile,
  domains: Domain[],
): SwissResource[] {
  return resources
    .filter((resource) =>
      resource.domains.some((domain) => domains.includes(domain))
      && resource.applies(profile))
    .sort((a, b) => {
      const officialDifference =
        Number(b.kind === "official-service" || b.kind === "official-guide")
        - Number(a.kind === "official-service" || a.kind === "official-guide");
      return officialDifference || b.score(profile) - a.score(profile);
    });
}
